import { ca } from '@cian/analytics';
import { TDevice } from '@cian/utils';

import { IJsonQuery } from '../../../../packages/api-models/common/json_query';
import { TPublicWebV1SearchResponse_MlRanking } from '../../../common/repositories/dailyrent/entities/response/PublicWebV1SearchResponse';

const OFFERS_ON_PAGE = 28;
const trackingReasonToListingType: Record<string, string> = {
  pin_click: 'map',
  all_objects_on_map: 'list',
};

interface ITrackOfferActionsProps {
  deviceType: TDevice;
  gaLabel?: string | null;
  isCianPartner?: boolean;
  jsonQuery: IJsonQuery;
  offerId: number;
  offerPosition: number;
  offersLength: number;
  pageNumber: number;
  partnerUrl?: string | null;
  queryString: string;
  reason: 'all_objects_on_map' | 'pin_click';
}

function getQueryString(queryString: string) {
  return window.location.search || queryString;
}

function getOffersQty(offersLength: number) {
  return offersLength < OFFERS_ON_PAGE ? offersLength : OFFERS_ON_PAGE;
}

function getPage({ offersLength, pageNumber, queryString, reason }: ITrackOfferActionsProps) {
  return {
    pageType: 'Listing',
    listingType: trackingReasonToListingType[reason],
    offersQty: getOffersQty(offersLength),
    pageNumber,
    extra: {
      reason,
      mobileWebsiteType: 'new',
    },
    queryString: getQueryString(queryString),
  };
}

function getPartnerExtra({ isCianPartner, partnerUrl }: ITrackOfferActionsProps) {
  return { isPartner: isCianPartner, partner_url: partnerUrl };
}

export function trackAddFavorite(props: ITrackOfferActionsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'favorite',
    action: props.deviceType === 'desktop' ? 'add_from_newlist' : 'add_from_list_mob',
    label: props.gaLabel,
    page: getPage(props),
    products: [
      {
        id: props.offerId,
        position: props.offerPosition,
        offerType: 'offer',
        extra: getPartnerExtra(props),
      },
    ],
    sc: props.jsonQuery,
  });
}

export function trackRemoveFromFavorite(props: ITrackOfferActionsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'favorite',
    action: props.deviceType === 'desktop' ? 'remove_from_newlist' : 'remove_from_list_mob',
    label: props.gaLabel,
    page: getPage(props),
    products: [{ id: props.offerId, position: props.offerPosition, offerType: 'offer' }],
    sc: props.jsonQuery,
  });
}

export function trackBookClick(props: ITrackOfferActionsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'Phones',
    action: 'Open_listing',
    label: props.gaLabel,
    page: getPage(props),
    products: [
      {
        id: props.offerId,
        position: props.offerPosition,
        offerType: 'offer',
        extra: getPartnerExtra(props),
      },
    ],
    sc: props.jsonQuery,
  });
}

export function trackCall(props: ITrackOfferActionsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'Phones',
    action: 'Call_listing',
    label: props.gaLabel,
    page: getPage(props),
    products: [
      {
        id: props.offerId,
        position: props.offerPosition,
        offerType: 'offer',
        extra: getPartnerExtra(props),
      },
    ],
    sc: props.jsonQuery,
  });
}

export function trackCardClick(props: ITrackOfferActionsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'Listing',
    action: 'to_card',
    label: 'card_detail_in_newlist',
    referrer: window.location.href,
    page: getPage(props),
    products: [
      {
        id: props.offerId,
        position: props.offerPosition,
        offerType: 'offer',
        extra: {
          isPartner: props.isCianPartner || undefined,
          partner_url: props.partnerUrl,
        },
      },
    ],
    sc: props.jsonQuery,
  });
}

export function trackChatClick(props: ITrackOfferActionsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'Listing',
    action: 'send_message',
    label: props.gaLabel,
    page: getPage(props),
    products: [
      {
        id: props.offerId,
        position: props.offerPosition,
        offerType: 'offer',
        extra: {
          isPartner: props.isCianPartner || undefined,
          partner_url: props.partnerUrl,
        },
      },
    ],
    sc: props.jsonQuery,
  });
}

export function trackMoreClick(props: ITrackOfferActionsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'DailyrentButton',
    action: 'Click',
    label: 'ToCard',
    page: getPage(props),
    products: [
      {
        id: props.offerId,
        position: props.offerPosition,
        offerType: 'offer',
        extra: getPartnerExtra(props),
      },
    ],
    sc: props.jsonQuery,
  });
}

export function trackShowPhoneClick(props: ITrackOfferActionsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'Phones',
    action: 'Open_listing_in_map',
    label: props.gaLabel,
    page: getPage(props),
    products: [
      {
        id: props.offerId,
        position: props.offerPosition,
        offerType: 'offer',
        extra: getPartnerExtra(props),
      },
    ],
    sc: props.jsonQuery,
  });
}

export function trackSlideChange(props: ITrackOfferActionsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'Listing',
    action: 'slide_photo',
    label: props.gaLabel,
    page: getPage(props),
    products: [{ id: props.offerId, position: props.offerPosition, offerType: 'offer' }],
    sc: props.jsonQuery,
  });
}

export function trackOfferCardShowOnMap(
  {
    deviceType,
    offerPosition,
    offerId,
    jsonQuery,
    pageNumber,
    offersLength,
    queryString,
    reason,
  }: ITrackOfferActionsProps,
  mlRanking: TPublicWebV1SearchResponse_MlRanking | undefined,
  mlSearchSessionGuid: string | null | undefined,
) {
  ca('teaser', {
    sc: jsonQuery,
    product: {
      screen: 'Map',
      block: 'MapSnippet',
      id: offerId,
      name: 'show_card',
      extra: {
        offerType: 'offer',
      },
      position: offerPosition,
    },
    page: {
      listingType: trackingReasonToListingType[reason],
      pageType: 'Listing',
      offersQty: offersLength,
      pageNumber,
      extra: {
        reason,
        mobileWebsiteType: 'new',
        mlSearchSessionGuid,
        mlRankingGuid: mlRanking?.guid,
        mlRankingModelVersion: mlRanking?.modelVersion,
      },
      siteType: deviceType === 'desktop' ? 'desktop' : 'mobile',
      queryString,
    },
  });
}
